import {
  ArrowRight,
  Check,
  CheckCheck,
  CircleHelp,
  ExternalLink,
  Lock,
  Plus,
  QrCode,
  RefreshCw,
  Save,
  Send,
  Shield,
  Trash2,
  Upload,
} from "lucide-react";
import type { DetailedHelpMap } from "./types";

/**
 * Ajuda detalhada: /configuracoes (8 abas), /configuracoes/equipe,
 * /configuracoes/carteira, /configuracoes/chaves-de-api, /rotinas,
 * /alertas-operacionais, /versoes, /mensagens, /configuracao-inicial,
 * /notificacoes. Conteúdo verificado lendo cada componente real (e o
 * controller Laravel correspondente) antes de escrever.
 *
 * Sobre `fact`: só /rotinas tem contadores de tela no backend
 * (HelpContextController::screenFacts). As outras telas daqui usam apenas os
 * fatos base — pagamentos, WhatsApp e propriedade —, que vêm em toda
 * consulta. Onde não há fato, não existe linha "Agora nesta pousada": é
 * melhor omitir do que inventar.
 */
export const CONFIGURACOES_HELP: DetailedHelpMap = {
  "/configuracoes#perfil": [
    {
      title: "O que esta aba faz",
      description:
        "Aqui você mexe na SUA conta de usuário, não na pousada. Nome, e-mail, foto e senha valem só para quem está logado agora — nenhum ajuste desta aba muda nada para o resto da equipe.",
      items: [
        {
          label: "Foto do perfil (avatar)",
          domKey: "perfil.avatar",
          visual: { icon: Upload, text: "Avatar", tone: "outline" },
          explanation:
            "Aceita PNG, JPG ou WEBP. É a imagem que aparece ao lado do seu nome nas telas em que o sistema mostra quem fez o quê — útil quando várias pessoas dividem o mesmo balcão.",
        },
        {
          label: "Nome, e-mail, nome de usuário e telefone",
          explanation:
            "O e-mail aqui é o mesmo usado para entrar no sistema e para receber a recuperação de senha. Trocar por um endereço ao qual você não tem acesso é o jeito mais fácil de ficar de fora da própria conta.",
        },
        {
          label: "Troca de senha",
          domKey: "perfil.senha",
          explanation:
            'Exige a senha atual para definir uma nova — sem ela o sistema não troca. Se você esqueceu a atual, saia e use "esqueci minha senha" na tela de login em vez de tentar por aqui.',
        },
        {
          label: "Salvar perfil",
          visual: { icon: Save, text: "Salvar perfil", tone: "primary" },
          explanation:
            "Nada nesta aba é gravado enquanto você não clicar aqui — inclusive a nova senha. Trocar de aba antes disso descarta o que foi digitado.",
        },
        {
          label: 'Botão "Ver tutorial novamente"',
          domKey: "perfil.tutorial",
          visual: { icon: CircleHelp, text: "Ver tutorial novamente", tone: "outline" },
          explanation:
            "Reabre o tour de apresentação do sistema (o mesmo do primeiro acesso), em formato de slides. Pode ser repetido quantas vezes quiser e é o caminho mais rápido para treinar alguém que acabou de chegar.",
        },
      ],
    },
  ],
  "/configuracoes#propriedade": [
    {
      title: "O que esta aba faz",
      description:
        "É o cadastro da pousada como empresa: o que sai impresso em voucher, nota e relatório vem daqui. Também é onde ficam duas escolhas que mudam o comportamento do sistema todo dia — o fuso horário e a moeda.",
      items: [
        {
          label: 'Botão "Buscar" ao lado do CNPJ',
          domKey: "propriedade.cnpj_buscar",
          visual: { icon: RefreshCw, text: "Buscar", tone: "outline" },
          explanation:
            "Consulta o CNPJ numa base pública externa e preenche sozinho os dados cadastrais da empresa. Poupa digitação e evita erro de razão social em documento fiscal — mas confira o resultado, porque a base pode estar desatualizada.",
        },
        {
          label: "Fuso horário",
          domKey: "propriedade.fuso",
          explanation:
            "Não é enfeite: é ele que define quando vira o dia de negócio na Auditoria Noturna e a que horas os registros aparecem nas telas operacionais. Fuso errado joga o fechamento do dia para a hora errada e faz a diária cair na data errada.",
          fact: (ctx) =>
            ctx.property.timezone
              ? `fuso configurado: ${ctx.property.timezone}.`
              : null,
        },
        {
          label: "Moeda",
          domKey: "propriedade.moeda",
          explanation:
            "Define a moeda em que os valores são exibidos e cobrados. A cobrança online passa pelo Asaas, que opera em real — trocar para outra moeda muda a exibição, não cria conversão automática de câmbio.",
          fact: (ctx) =>
            ctx.property.currency
              ? `moeda configurada: ${ctx.property.currency}.`
              : null,
        },
        {
          label: "Salvar propriedade",
          visual: { icon: Save, text: "Salvar propriedade", tone: "primary" },
          explanation:
            "Grava os dados no hotel atual. Se você opera mais de uma pousada, confira o seletor do topo antes de salvar — o ajuste vai para a propriedade selecionada, não para todas.",
        },
      ],
    },
  ],
  "/configuracoes#pagamentos": [
    {
      title: "O que esta aba faz",
      description:
        "Decide QUAIS formas de pagamento a recepção pode usar ao fechar a conta do hóspede. O que for desligado aqui some dos fluxos de cobrança e também é recusado pela API do PMS. É diferente da Conta Asaas em Financeiro, que mostra saldo e extrato — aqui não se vê dinheiro, se define regra.",
      items: [
        {
          label: "PIX",
          domKey: "pagamentos.pix",
          explanation:
            "Cobrança instantânea por QR Code, gerada na hora do fechamento da conta. Depende da subconta Asaas do hotel estar aprovada — sem isso o método aparece na tela mas a cobrança não sai.",
          fact: (ctx) => {
            const ligado = ctx.payments.accepted_methods.pix;
            if (!ligado) return "desligado — o PIX não aparece como opção no fechamento de conta.";
            return ctx.payments.asaas_configured
              ? "ligado, com a conta Asaas configurada."
              : "ligado, mas a conta Asaas ainda não está configurada — a cobrança PIX não vai sair até isso ser resolvido.";
          },
        },
        {
          label: "Cartão de crédito",
          domKey: "pagamentos.credit",
          explanation:
            "Cobrança por cartão processada pelo Asaas (link de pagamento ou cartão já guardado do hóspede). Também depende da subconta Asaas aprovada.",
          fact: (ctx) => {
            const ligado = ctx.payments.accepted_methods.credit;
            if (!ligado) return "desligado — cartão de crédito não aparece como opção no fechamento de conta.";
            return ctx.payments.asaas_configured
              ? "ligado, com a conta Asaas configurada."
              : "ligado, mas a conta Asaas ainda não está configurada — a cobrança no cartão não vai sair até isso ser resolvido.";
          },
        },
        {
          label: "Cartão de débito",
          domKey: "pagamentos.debit",
          explanation:
            "Registro de uma cobrança feita na maquininha presencial. É só um registro no sistema para a conta do hóspede fechar — o dinheiro passa pela sua maquininha, não pelo Asaas, e não aparece na Carteira.",
          fact: (ctx) => (ctx.payments.accepted_methods.debit ? "ligado." : "desligado — não aparece no fechamento de conta."),
        },
        {
          label: "Dinheiro",
          domKey: "pagamentos.cash",
          explanation:
            "Pagamento em espécie na recepção. O sistema calcula o troco a partir do valor recebido. Desligar isso numa pousada que recebe dinheiro trava o fechamento de conta no balcão.",
          fact: (ctx) => (ctx.payments.accepted_methods.cash ? "ligado." : "desligado — não aparece no fechamento de conta."),
        },
        {
          label: "Transferência",
          domKey: "pagamentos.transfer",
          explanation:
            "Registro de TED/DOC ou transferência externa já recebida. Também é só registro: o sistema não confere se o dinheiro caiu, quem confere é você antes de marcar.",
          fact: (ctx) => (ctx.payments.accepted_methods.transfer ? "ligado." : "desligado — não aparece no fechamento de conta."),
        },
        {
          label: "Salvar métodos",
          visual: { icon: Save, text: "Salvar métodos", tone: "primary" },
          explanation:
            "Os interruptores acima só passam a valer depois deste clique. Sair da aba sem salvar mantém a configuração antiga.",
        },
        {
          label: "Conexão Asaas (bloco somente leitura)",
          domKey: "pagamentos.conexao_asaas",
          explanation:
            'Mostra três coisas que você não edita aqui: o ambiente (produção ou homologação), se a chave do hotel está configurada e se a notificação automática da plataforma está ligada. É o lugar de olhar quando "a cobrança não sai" — se a chave estiver ausente, o problema é esse.',
          fact: (ctx) =>
            ctx.payments.asaas_environment === "production"
              ? "ambiente de produção — as cobranças geradas são reais."
              : "ambiente de homologação (sandbox) — as cobranças geradas aqui são de teste e não movimentam dinheiro real.",
        },
        {
          label: "Emissão de NFS-e da propriedade",
          domKey: "pagamentos.nfse",
          explanation:
            "Configura o CNPJ do hotel para emitir nota de serviço ao hóspede. Os campos exigidos vêm da prefeitura cadastrada na subconta Asaas — por isso o formulário muda de município para município e pode pedir certificado digital A1, token da prefeitura ou usuário e senha. Os segredos vão direto para o Asaas e não ficam guardados no Innsight. Enquanto isso estiver pendente, o hotel cobra normalmente mas não emite nota.",
        },
        {
          label: "Distribuição do líquido do hotel (split interno)",
          domKey: "pagamentos.split_interno",
          explanation:
            "Aparece só para a conta mestre e para o Proprietário. Divide o valor que sobra para o hotel DEPOIS do repasse da plataforma entre filiais, sócios ou parceiros — cada destinatário com sua própria carteira Asaas. A soma dos repasses ativos não pode passar de 100% do líquido; sem nenhuma regra cadastrada, tudo fica na conta principal.",
          visual: { icon: Plus, text: "Novo repasse", tone: "outline" },
        },
        {
          label: '"Abrir Painel Allmight" (repasse global)',
          domKey: "pagamentos.painel_allmight",
          visual: { icon: ExternalLink, text: "Abrir Painel Allmight", tone: "outline" },
          explanation:
            "Aparece só para a conta mestre. O percentual que a InnSight retém é regra da plataforma e se configura lá fora, nunca dentro das configurações de um hotel.",
        },
      ],
    },
  ],
  "/configuracoes#integracoes": [
    {
      title: "O que esta aba faz",
      description:
        "Guarda as credenciais técnicas que ligam a pousada a serviços de fora: o registro oficial de hóspedes, a subconta de cobrança e o WhatsApp. Regra geral: senha salva aqui nunca volta para a tela — o campo serve para SUBSTITUIR, não para conferir.",
      items: [
        {
          label: "Usuário e senha SNRHOS (FNRH)",
          domKey: "integracoes.snrhos",
          explanation:
            "Credencial do sistema oficial de registro de hóspedes (Embratur). É o que permite enviar as fichas sem redigitar em outro site. O selo ao lado do título diz se já existe senha gravada; o campo abaixo só troca a que está lá.",
        },
        {
          label: "Chave da subconta Asaas",
          domKey: "integracoes.asaas",
          explanation:
            "Somente leitura. Essa chave é criada e rotacionada exclusivamente pelo Allmight na Central de Propriedades — não é para colar aqui a chave da conta-mãe. Se o selo indicar chave ausente, é com o suporte da plataforma, não com você.",
        },
        {
          label: "Habilitar conexão Evolution API",
          domKey: "integracoes.whatsapp_enabled",
          explanation:
            "Liberação técnica da instância: status, pareamento e envio. Este interruptor NÃO escolhe quais mensagens saem — isso é decidido nas automações abaixo e na aba Notificações. Com ele desligado, nada sai por WhatsApp, por mais que os eventos estejam marcados.",
          fact: (ctx) =>
            ctx.whatsapp.enabled
              ? "conexão habilitada."
              : "conexão desabilitada — nenhuma mensagem sai por WhatsApp enquanto isso.",
        },
        {
          label: "Nome da instância da Evolution API",
          domKey: "integracoes.whatsapp_instance",
          explanation:
            "URL e chave de acesso da Evolution são fixas na infraestrutura da plataforma — só o nome da instância desta pousada é configurável aqui. É esse nome que identifica o número de WhatsApp do hotel no serviço de envio.",
          fact: (ctx) => {
            if (!ctx.whatsapp.instance) return "nenhuma instância salva ainda — o envio por WhatsApp não vai funcionar até nomear e parear uma.";
            return ctx.whatsapp.enabled
              ? `instância "${ctx.whatsapp.instance}", com o envio habilitado.`
              : `instância "${ctx.whatsapp.instance}" salva, mas o envio está desabilitado — nenhuma mensagem sai enquanto isso.`;
          },
        },
        {
          label: "Mensagem padrão da reserva",
          domKey: "integracoes.template_reserva",
          explanation:
            "Texto usado quando o sistema manda os dados de uma reserva por WhatsApp. As chaves entre { } são substituídas na hora do envio: {hospede}, {codigo_reserva}, {checkin}, {checkout}, {acomodacao}, {total}, {saldo} e {portal}. Escrever a chave errada não quebra o envio — ela simplesmente sai como texto cru para o hóspede ler.",
        },
        {
          label: "Botões de conexão do WhatsApp",
          domKey: "integracoes.whatsapp_acoes",
          visual: { icon: QrCode, text: "Código QR", tone: "outline" },
          explanation:
            'A ordem que funciona é: salvar, gerar o "Código QR", ler o QR no celular do hotel e depois conferir em "Status". "Configurar webhook" avisa a Evolution para onde mandar as mensagens que chegam — sem isso o hotel envia, mas não recebe resposta dentro do sistema.',
        },
        {
          label: "Painel de pareamento (QR Code)",
          domKey: "integracoes.pareamento",
          explanation:
            "Mostra o QR gerado, e às vezes um código de pareamento numérico para digitar direto no aparelho. O QR expira em pouco tempo: se demorar entre gerar e ler, clique de novo em Código QR em vez de insistir no antigo.",
        },
        {
          label: "Horário permitido de envio (quiet hours)",
          domKey: "integracoes.quiet_hours",
          explanation:
            "Janela em que as mensagens automáticas podem sair. Fora dela o disparo é segurado, para o hóspede não receber cobrança às três da manhã. Vale para as automações — não é um bloqueio de mensagem escrita à mão pela recepção.",
        },
        {
          label: "Automações de comunicação por evento",
          domKey: "integracoes.automacoes",
          explanation:
            "Nove momentos da jornada do hóspede — reserva confirmada, lembrete de pré-check-in, pagamento pendente, pagamento confirmado, lembrete de chegada, boas-vindas no check-in, aviso de checkout, pedido de avaliação e recuperação de hóspede. Cada um liga/desliga sozinho e tem seu próprio texto e canal (WhatsApp, e-mail ou os dois). Só \"reserva confirmada\" já vem ativo; os outros começam desligados de propósito.",
        },
        {
          label: "Telefone e mensagem de teste",
          domKey: "integracoes.whatsapp_teste",
          visual: { icon: Send, text: "Enviar", tone: "outline" },
          explanation:
            "Manda uma mensagem real para o número informado. É o jeito de confirmar que o pareamento funcionou antes de ligar as automações e descobrir no hóspede que não estava funcionando.",
        },
        {
          label: "Salvar SNRHOS e Asaas",
          visual: { icon: Save, text: "Salvar SNRHOS e Asaas", tone: "primary" },
          explanation:
            'Este botão grava só o bloco SNRHOS. As configurações de WhatsApp têm o botão próprio "Salvar WhatsApp" logo acima — salvar um não salva o outro.',
        },
      ],
    },
  ],
  "/configuracoes#seguranca": [
    {
      title: "O que esta aba faz",
      description:
        "Adiciona uma segunda tranca na SUA conta: além da senha, um código que muda a cada 30 segundos no seu celular. Vale só para você — ativar aqui não obriga o resto da equipe. Para quem tem acesso a dinheiro e cadastro de hóspede, é a diferença entre uma senha vazada virar prejuízo ou não.",
      items: [
        {
          label: "Ativar 2FA",
          domKey: "seguranca.ativar_2fa",
          visual: { icon: Shield, text: "Ativar 2FA", tone: "primary" },
          explanation:
            "Começa a configuração e mostra o QR Code. Atenção: clicar aqui ainda NÃO protege a conta — o 2FA só passa a valer depois de confirmar com um código do aplicativo.",
        },
        {
          label: "QR Code, chave manual e confirmação",
          domKey: "seguranca.setup_2fa",
          visual: { icon: Check, text: "Confirmar 2FA", tone: "primary" },
          explanation:
            "Leia o QR num aplicativo autenticador (Google Authenticator, Authy e similares) ou copie a chave manual se a câmera falhar. Depois digite os 6 dígitos que o app mostra e confirme. Se você fechar a tela sem confirmar, nada foi ativado e é preciso começar de novo.",
        },
        {
          label: "Códigos de recuperação",
          domKey: "seguranca.codigos_recuperacao",
          explanation:
            "Mostrados uma única vez, logo depois de ativar. São a saída de emergência para quando o celular do autenticador for perdido, trocado ou formatado. Guarde fora do celular — se perder os dois, recuperar a conta vira caso de suporte.",
        },
        {
          label: "Desativar 2FA",
          domKey: "seguranca.desativar_2fa",
          visual: { icon: Lock, text: "Desativar", tone: "danger" },
          explanation:
            "Exige a senha atual da conta para confirmar, justamente para que ninguém que sente no seu computador aberto consiga baixar a proteção. Desativar apaga o vínculo com o app: religar depois gera um QR novo.",
        },
      ],
    },
  ],
  "/configuracoes#notificacoes": [
    {
      title: "O que esta aba faz",
      description:
        "É o liga/desliga simples dos dois avisos que o hóspede recebe quando o pagamento é confirmado: voucher por e-mail e voucher/pré-check-in por WhatsApp. Para controlar evento por evento com texto próprio, o lugar é a aba Integrações — esta aqui é a versão resumida.",
      items: [
        {
          label: "Selos de pré-requisito do WhatsApp",
          domKey: "notificacoes.prerequisito",
          visual: { icon: ExternalLink, text: "Configurar conexão", tone: "outline" },
          explanation:
            'Dizem se a conexão está habilitada e se a instância parece conectada. O primeiro selo é decisivo: desabilitado, nada sai. O segundo, "instância conectada", é informativo — o sistema tenta enviar mesmo que ele indique desconectado, então não conclua que está tudo bem só porque ele está verde.',
          fact: (ctx) =>
            ctx.whatsapp.enabled
              ? "conexão WhatsApp habilitada nesta pousada."
              : "conexão WhatsApp desabilitada — habilite em Integrações antes de contar com o envio.",
        },
        {
          label: "Enviar confirmação por e-mail",
          domKey: "notificacoes.email",
          explanation:
            "Dispara o voucher e os dados da reserva depois que o pagamento é confirmado. É o canal mais confiável para o hóspede guardar o comprovante — não depende de pareamento nem de número certo.",
        },
        {
          label: "Enviar confirmação por WhatsApp",
          domKey: "notificacoes.whatsapp",
          explanation:
            "Mesma confirmação pelo WhatsApp, com o pré-check-in. Só funciona se a conexão da aba Integrações estiver habilitada e pareada — ligar aqui com a conexão desligada não produz nada.",
          fact: (ctx) =>
            ctx.whatsapp.enabled
              ? null
              : "a conexão do WhatsApp está desabilitada agora — ligar esta opção não vai enviar nada até resolver isso em Integrações.",
        },
        {
          label: "Salvar envios automáticos",
          visual: { icon: Save, text: "Salvar envios automáticos", tone: "primary" },
          explanation: "Os dois interruptores só valem depois deste clique.",
        },
      ],
    },
  ],
  "/configuracoes#aparencia": [
    {
      title: "O que esta aba faz",
      description:
        "Define a cara do sistema para TODA a equipe desta pousada — não é preferência pessoal. Trocar o tema aqui muda o painel de todo mundo que trabalha nesta propriedade.",
      items: [
        {
          label: "Logo da propriedade",
          domKey: "aparencia.logo",
          visual: { icon: Upload, text: "Enviar PNG/JPG/WEBP", tone: "outline" },
          explanation:
            "Aparece na navegação da equipe, na vitrine pública e nos documentos identificados (voucher, notas). O nome \"Innsight\" continua aparecendo apenas como assinatura da plataforma — a logo não o substitui.",
        },
        {
          label: "Tema (Escuro operacional / Claro administrativo)",
          domKey: "aparencia.tema",
          explanation:
            "Escuro cansa menos no turno da noite na recepção; claro tem mais contraste em sala iluminada. A escolha é salva para a PROPRIEDADE, não por usuário — combine com a equipe antes de trocar.",
        },
        {
          label: "Cor de destaque",
          domKey: "aparencia.cor",
          explanation:
            "Aplicada em botões e detalhes de toda a interface interna. É separada da cor da vitrine pública, que fica na aba Motor de Reservas / Site — mudar uma não muda a outra.",
        },
        {
          label: "Salvar aparência",
          visual: { icon: Save, text: "Salvar aparência", tone: "primary" },
          explanation:
            "A pré-visualização é imediata ao clicar nas opções, mas só este botão grava. Se você trocou o tema, gostou e saiu sem salvar, ele volta ao anterior no próximo carregamento.",
        },
      ],
    },
  ],
  "/configuracoes#site": [
    {
      title: "O que esta tela faz",
      description:
        "Monta a página pública onde o hóspede reserva direto com o hotel, sem comissão de OTA. A disponibilidade e as reservas vêm do próprio PMS — o que você edita aqui é o endereço, a apresentação e os argumentos de venda. O que estiver salvo é o que o hóspede vê.",
      items: [
        {
          label: "Domínio próprio",
          domKey: "site.dominio_proprio",
          explanation:
            "Cadastrar o domínio aqui não o ativa: é preciso ter registrado o domínio e apontado o DNS antes de divulgá-lo. Enquanto isso não estiver pronto, use o subdomínio Innsight nos links — o domínio próprio passa a ser o endereço principal automaticamente quando estiver válido.",
        },
        {
          label: 'Botão "Verificar domínio"',
          domKey: "site.verificar_dominio",
          visual: { icon: RefreshCw, text: "Verificar domínio", tone: "outline" },
          explanation:
            'Consulta se o domínio já foi registrado por alguém. Cuidado com a leitura: "registrado" diz apenas que o domínio existe, não que ele aponta para cá — o apontamento DNS é um passo à parte.',
        },
        {
          label: "Subdomínio Innsight",
          domKey: "site.subdominio",
          explanation:
            "Endereço de apoio (ex.: pousadadapaz.innsight.com.br) que funciona desde o primeiro dia. Serve também para testar a vitrine antes de trocar os links públicos para o domínio próprio.",
        },
        {
          label: "Cor de destaque, imagem principal, título e complemento",
          explanation:
            "Identidade visual da vitrine — separada da cor do painel interno, que fica na aba Aparência. A imagem principal é uma URL pública: se o endereço da foto sair do ar, o topo do site fica sem imagem.",
        },
        {
          label: "Destaques da vitrine",
          domKey: "site.destaques",
          explanation:
            "Três cards com os motivos para reservar direto. O ícone é informado pelo NOME (Coffee, Wifi, Car, Waves, ShieldCheck, CalendarDays, MessageCircle); nome desconhecido cai num ícone genérico em vez de quebrar a página. São sempre três — não dá para ter mais nem menos.",
        },
        {
          label: "Prévia da vitrine",
          domKey: "site.previa",
          explanation:
            "Atualiza enquanto você digita, mas é só ensaio: o hóspede continua vendo a última versão salva. Confira aqui o texto e a imagem antes de publicar.",
        },
        {
          label: "Recebimento online pendente",
          domKey: "site.pagamento_pendente",
          visual: { icon: ExternalLink, text: "Configurar pagamentos", tone: "outline" },
          explanation:
            "Este atalho só aparece quando o recebimento online ainda não está configurado. Enquanto estiver assim, o site pode até apresentar a pousada, mas a reserva não se paga sozinha — alguém vai ter que cobrar à parte.",
          fact: (ctx) =>
            ctx.payments.asaas_configured
              ? null
              : "a conta Asaas desta pousada ainda não está configurada — o pagamento online da vitrine não vai funcionar.",
        },
        {
          label: "Salvar e publicar",
          visual: { icon: Save, text: "Salvar e publicar", tone: "primary" },
          explanation:
            "Publica na hora: não existe rascunho nem agendamento. O que for salvo fica imediatamente visível para quem abrir o endereço público.",
        },
      ],
    },
  ],
  "/configuracoes/equipe": [
    {
      title: "O que esta tela faz",
      description:
        "Controla quem entra no painel desta pousada e o que cada um enxerga. O papel não é um rótulo: é ele que decide se a pessoa vê financeiro, mexe em tarifa ou só cuida da limpeza. Dar Gerente para todo mundo é o jeito mais rápido de perder o controle de quem alterou o quê.",
      items: [
        {
          label: 'Botão "Adicionar Membro"',
          domKey: "equipe.adicionar",
          visual: { icon: Plus, text: "Adicionar Membro", tone: "primary" },
          explanation:
            "Pede nome, e-mail e papel. O usuário é criado na hora com uma senha aleatória e recebe um e-mail com link para definir a própria senha — você nunca digita a senha dele. Se o e-mail não chegar, o cadastro JÁ existe: não crie de novo, peça para a pessoa usar \"esqueci minha senha\" na tela de login.",
        },
        {
          label: "Papéis disponíveis",
          explanation:
            "Gerente, Recepcionista, Governança e Colaborador. O papel de Proprietário não está na lista de propósito — ele só é atribuído pela conta mestre da plataforma, para que ninguém dentro do hotel se promova sozinho.",
        },
        {
          label: "Seletor de papel na linha do membro",
          domKey: "equipe.papel",
          explanation:
            "Troca o papel direto na lista, sem abrir formulário, e vale na hora. A setinha some na linha do Proprietário: aquele papel não é rebaixável por esta tela. Trocar o papel de alguém que está logado muda o que ele vê no próximo carregamento.",
        },
        {
          label: "Remover membro (ícone de lixeira)",
          domKey: "equipe.remover",
          visual: { icon: Trash2, text: "Remover membro", tone: "danger" },
          explanation:
            "Apaga o usuário em definitivo — não é uma desativação temporária e não há desfazer. Você não consegue remover a si mesmo, mas a tela NÃO impede remover um Proprietário: confira o papel na linha antes de clicar. Para afastar alguém por pouco tempo, prefira rebaixar o papel a excluir.",
        },
      ],
    },
  ],
  "/configuracoes/carteira": [
    {
      title: "O que esta tela faz",
      description:
        "É um resumo rápido do dinheiro de hóspede ligado a esta pousada, com um atalho para sacar. Os três números do topo são ESTIMATIVAS calculadas a partir das suas reservas — não são uma consulta ao saldo real da conta Asaas. Para conferência contábil, o lugar certo é Financeiro → Conta Asaas.",
      items: [
        {
          label: "Saldo disponível",
          domKey: "carteira.saldo",
          explanation:
            "Estimativa a partir das reservas do mês corrente (excluindo canceladas e no-show), já com um desconto aproximado de taxa. Serve para ter uma ordem de grandeza — não bate necessariamente com o que o Asaas tem para liberar hoje.",
          fact: (ctx) =>
            ctx.payments.asaas_configured
              ? null
              : "a subconta Asaas desta pousada não está configurada — sem ela não há saque possível, por mais que o número acima apareça.",
        },
        {
          label: "A confirmar",
          domKey: "carteira.a_confirmar",
          explanation:
            "Apesar do rótulo \"aguardando compensação\", este número é o total das reservas com check-out nos últimos 30 dias. Leia como indicador de volume recente, não como uma parcela específica que o banco vai liberar em tal dia.",
        },
        {
          label: "Recebido este mês",
          domKey: "carteira.recebido_mes",
          explanation:
            "Soma do valor das reservas criadas neste mês. Repare: é o valor das reservas, não o dinheiro que efetivamente entrou — uma reserva feita e ainda não paga já conta aqui.",
        },
        {
          label: '"Levantar fundos"',
          domKey: "carteira.levantar",
          visual: { text: "Levantar fundos", tone: "outline" },
          explanation:
            "Este é o único ponto da tela que mexe com dinheiro de verdade: abre o formulário de transferência (banco, agência, conta, tipo) e envia a ordem para o Asaas. Como o saldo mostrado acima é estimado, o Asaas pode recusar se o saldo real dele for menor — e a recusa vem com a mensagem do próprio Asaas.",
        },
        {
          label: "Extrato",
          domKey: "carteira.extrato",
          explanation:
            "Lista montada a partir das últimas reservas da pousada, uma linha por reserva. Não é o extrato oficial da conta Asaas: taxas, estornos e transferências não aparecem aqui.",
        },
      ],
    },
  ],
  "/configuracoes/chaves-de-api": [
    {
      title: "O que esta tela faz",
      description:
        "Cria credenciais para que um sistema de fora (um ERP, um app próprio, uma automação) converse com o Innsight sem usar o login de uma pessoa. A vantagem prática: se a integração for desligada ou o parceiro trocar, você revoga a chave dela sem mexer na conta de ninguém.",
      items: [
        {
          label: 'Botão "Nova Key"',
          domKey: "chaves.nova",
          visual: { icon: Plus, text: "Nova Key", tone: "primary" },
          explanation:
            "Abre o formulário de criação: nome identificador, escopo pretendido e data de expiração opcional. Dê um nome que diga QUEM usa a chave (\"ERP contábil\", \"app da recepção\") — daqui a seis meses é esse nome que vai decidir se você pode revogá-la com segurança.",
        },
        {
          label: "Permissões (Leitura / Escrita / Webhooks)",
          domKey: "chaves.permissoes",
          explanation:
            "Marcam o escopo pretendido daquela integração e ficam gravadas junto com a chave, servindo de documentação de para que ela foi criada. Não substituem o cuidado com quem recebe a chave.",
        },
        {
          label: "Expiração (opcional)",
          domKey: "chaves.expiracao",
          explanation:
            "Data informada junto com a chave e exibida na lista com um alerta quando se aproxima. É um lembrete de rotação — não conte com ela para desligar sozinha uma integração que precisa parar: para isso, revogue.",
        },
        {
          label: "Exibição única da chave criada",
          domKey: "chaves.exibicao_unica",
          explanation:
            "A chave completa aparece UMA vez, no momento da criação, e depois some para sempre — o sistema guarda só um resumo criptografado dela. Copie e cole no destino antes de dispensar o aviso. Se perder, o único caminho é revogar e criar outra.",
        },
        {
          label: "Interruptor de ativa/inativa",
          domKey: "chaves.alternar",
          explanation:
            "Desativa a chave sem apagá-la. É o que você usa para testar se alguma integração ainda depende dela: desative, espere, e reative se algo reclamar — sem perder o registro.",
        },
        {
          label: 'Botão "Revogar"',
          domKey: "chaves.revogar",
          visual: { icon: Trash2, text: "Revogar", tone: "danger" },
          explanation:
            "Apaga a chave em definitivo, sem desfazer. Qualquer integração que a usasse para de funcionar na hora. Se a intenção é só pausar, use o interruptor ao lado em vez disto.",
        },
      ],
    },
  ],
  "/rotinas": [
    {
      title: "O que esta tela faz",
      description:
        "Lista os processos que o sistema executa sozinho, sem ninguém clicar: Auditoria Noturna, Sincronização de canais, Regras de Yield, Processamento de No-Shows, Lembretes de conta, Estatísticas Diárias, Sync de Inventário e Sincronização de Licença. Aqui você vê se rodaram, quando rodaram e o que deu errado — não é uma tela de execução manual, não existe botão de \"rodar agora\" nela.",
      items: [
        {
          label: "Cartões do topo (total, ativas, com falha)",
          domKey: "rotinas.kpis",
          explanation:
            'Leitura rápida do dia. O número em "Com falha" é o que importa de manhã: se estiver acima de zero, abra o card correspondente e leia o último erro antes de confiar nos números de ocupação e receita.',
          fact: (ctx) => {
            const { routines_total: total, routines_active: ativas } = ctx.screen;
            if (total === undefined || ativas === undefined) return null;
            if (total === 0) return "nenhuma rotina registrada para esta propriedade ainda.";
            return `${ativas} de ${total} rotina(s) ativa(s).`;
          },
        },
        {
          label: "Interruptor de ativa/inativa por rotina",
          domKey: "rotinas.interruptor",
          explanation:
            "Na Auditoria Noturna, desligar o interruptor impede a execução automática no horário programado — é a única rotina que de fato consulta esse interruptor antes de rodar. As outras da lista dependem de jobs próprios da plataforma que não checam essa chave, então desligar ali não garante que o processo pare. Execuções feitas à mão em outras telas nunca são afetadas por ele.",
        },
        {
          label: "Frequência e hora de execução",
          domKey: "rotinas.configuracao",
          explanation:
            "Aparecem ao expandir o card, na setinha à direita. A frequência aceita diária, semanal ou mensal, e a hora usa o formato HH:MM (ex.: 02:00) no fuso configurado na aba Propriedade. A hora só é gravada ao clicar em Salvar ao lado do campo; a frequência salva assim que você escolhe.",
        },
        {
          label: "Último erro",
          domKey: "rotinas.ultimo_erro",
          explanation:
            "Mensagem técnica da última execução que falhou, dentro do card expandido. Vale copiar inteira ao abrir um chamado — é ela que diz se o problema foi o canal de venda fora do ar, um dado inconsistente ou uma falha do sistema.",
        },
      ],
    },
  ],
  "/alertas-operacionais": [
    {
      title: "O que esta tela faz",
      description:
        "É a conferência automática do sistema contra ele mesmo: em vez de esperar alguém notar que um número está errado, ele varre reservas, contas e receita procurando incoerências. Nada aqui é configurável e nada é opinião — são achados objetivos, do tipo \"esta reserva deveria ter feito check-out ontem e ainda está hospedada\". Se a lista está vazia, os dados fecham.",
      items: [
        {
          label: 'Botão "Verificar agora"',
          domKey: "anomalias.verificar",
          visual: { icon: RefreshCw, text: "Verificar agora", tone: "outline" },
          explanation:
            "Roda a varredura de novo nas três áreas. O sistema não guarda uma lista pronta de anomalias: ele recalcula tudo a cada consulta, sobre os dados de agora. Por isso, depois de corrigir uma reserva, basta clicar aqui e a linha deve sumir.",
        },
        {
          label: "Aba Reservas",
          domKey: "anomalias.aba.reservas",
          explanation:
            'Quatro achados: "Saídas Pendentes" (hóspede ainda hospedado com data de saída já vencida), "Candidatos a No-Show" (reserva confirmada cuja chegada já passou sem check-in), "Pendentes Antigas" (reserva nunca confirmada com chegada no passado) e "Overbooking Detectado" (mesmo quarto com duas reservas em datas que se sobrepõem). Overbooking é o único que costuma virar problema com o hóspede no balcão — trate primeiro.',
        },
        {
          label: "Aba Contas da hospedagem",
          domKey: "anomalias.aba.contas",
          explanation:
            'Quatro achados: conta ainda aberta de hóspede que já foi embora, conta com saldo negativo (o hotel devendo ao hóspede, quase sempre erro de lançamento), hóspede com check-in feito e sem conta principal aberta, e conta fechada com saldo diferente de zero. Todos apontam para dinheiro que não vai fechar no relatório se ficar assim.',
        },
        {
          label: "Aba Receita",
          domKey: "anomalias.aba.receita",
          explanation:
            'Três achados: dias sem estatística consolidada nos últimos 30 dias, dias com quarto ocupado e receita zerada, e hóspedes em casa cuja conta não recebeu lançamento hoje. Os três costumam ter a mesma causa: a Auditoria Noturna não rodou ou falhou — vale conferir /rotinas antes de sair investigando reserva por reserva.',
        },
        {
          label: "Contadores nas abas e nos cartões",
          explanation:
            "Mostram quantas divergências existem em cada área agora. Aba sem número é aba sem pendência — não precisa abrir.",
        },
      ],
    },
  ],
  "/versoes": [
    {
      title: "O que esta tela faz",
      description:
        "Mostra o que mudou em cada versão do Innsight e qual está rodando agora. Para o hotel, serve para responder \"isso mudou ou eu que estou fazendo errado?\" depois de uma atualização. Publicar versão é coisa da plataforma — os botões de publicação só aparecem para a conta mestre.",
      items: [
        {
          label: "Faixa de situação da versão",
          domKey: "versoes.banner_versao",
          explanation:
            "Compara a versão em execução com a última registrada. Verde significa atualizado; amarelo indica que existe versão mais nova publicada do que a que está no ar. À direita, em fonte monoespaçada, aparece exatamente qual versão está rodando — é esse número que o suporte vai pedir.",
        },
        {
          label: "Filtros por tipo",
          domKey: "versoes.filtros",
          explanation:
            "Todas, Estável, Beta, Correção urgente e Reversão. Filtrar por \"Correção urgente\" é o caminho mais rápido para descobrir se um problema que você relatou já foi corrigido.",
        },
        {
          label: "Notas de cada versão",
          explanation:
            "Clique na setinha do card para abrir o texto completo do que mudou. As notas são texto corrido, sem formatação — termos técnicos do changelog são traduzidos automaticamente para a linguagem do hotel ao exibir.",
        },
        {
          label: 'Botão "Registrar versão"',
          domKey: "versoes.registrar",
          visual: { icon: Plus, text: "Registrar versão", tone: "primary" },
          explanation:
            "Só para o dono do sistema (Allmight). Abre o modal \"Registrar nova versão\", que publica uma entrada no changelog com número, tipo (estável, beta, correção urgente ou reversão), título, responsável e notas. Duas marcações mudam o comportamento da tela: \"Mudanças incompatíveis\" coloca o selo vermelho de quebra de compatibilidade, e \"Marcar como versão atual\" define qual versão a faixa de situação passa a considerar como no ar.",
        },
        {
          label: 'Botão "Definir atual"',
          domKey: "versoes.definir_atual",
          explanation:
            "Só para o dono do sistema, e só aparece em versões que não são a atual. Move o selo \"Em produção\" para aquela versão — é registro, não faz deploy nem reverte código.",
        },
        {
          label: "Inventário técnico",
          domKey: "versoes.inventario",
          explanation:
            "Painel visível apenas para o dono do sistema, com o inventário do que foi entregue. Não é informação operacional do hotel.",
        },
      ],
    },
  ],
  "/mensagens": [
    {
      title: "O que esta tela faz",
      description:
        "Junta dois canais que não se misturam: conversas internas entre a equipe (recado de turno, aviso para a governança) e o WhatsApp real do hóspede. Saber em qual dos dois você está é o que evita mandar para o hóspede um recado que era para a camareira.",
      items: [
        {
          label: '"Nova conversa" (mensagens internas)',
          domKey: "mensagens.nova_conversa",
          visual: { icon: Plus, text: "Nova conversa", tone: "primary" },
          explanation:
            "Cria uma conversa da própria pousada ou de um setor dela — Recepção, Governança ou Gestão. Não há seletor de outra pousada e não há hóspede aqui: é comunicação interna. A lista se atualiza sozinha, sem precisar recarregar a página.",
        },
        {
          label: "Aba WhatsApp externo",
          domKey: "mensagens.whatsapp",
          explanation:
            "Mostra as mensagens reais trocadas pelo número comercial da pousada, com o status de cada uma. Este botão não aparece para a conta mestre — o Allmight acompanha o WhatsApp pelo painel próprio dele, não por aqui.",
          fact: (ctx) =>
            ctx.whatsapp.enabled
              ? null
              : "a conexão WhatsApp desta pousada está desabilitada — mensagens novas não serão enviadas até religar em Configurações → Integrações.",
        },
        {
          label: 'Botão "Reenviar"',
          domKey: "mensagens.reenviar",
          explanation:
            "Coloca de novo na fila uma mensagem que falhou (o motivo da falha aparece em vermelho logo acima). Responder pelo campo de baixo também entra na fila de envio pela Evolution, e não sai na hora — o status da mensagem é quem confirma que saiu.",
        },
      ],
    },
  ],
  "/configuracao-inicial": [
    {
      title: "O que esta tela faz",
      description:
        "Assistente para tirar uma pousada nova do zero em cinco passos: Propriedade, Quartos, Tarifas, Integrações e Concluído. Cada passo grava no sistema ao avançar — não é uma prévia que só se confirma no fim. Se você sair no meio, o que já passou está salvo; o que estava na tela, não.",
      items: [
        {
          label: "Passo Propriedade",
          domKey: "assistente.passo.propriedade",
          explanation:
            "Nome, CNPJ, endereço e telefone da pousada. São os dados que vão sair em voucher e documento — vale conferir agora em vez de descobrir o erro no primeiro comprovante emitido.",
        },
        {
          label: "Passo Quartos",
          domKey: "assistente.passo.quartos",
          explanation:
            "Cada quarto tem nome/número, tipo (Standard, Superior, Deluxe, Suíte ou Família), capacidade e diária. Ao clicar em Próximo, todos são criados de verdade — não precisa recriá-los depois na tela de Ocupação. Cadastrar errado aqui significa apagar e refazer lá.",
        },
        {
          label: "Passo Tarifas",
          domKey: "assistente.passo.tarifas",
          explanation:
            "Define a diária base e dois multiplicadores aplicados por cima dela: fim de semana e alta temporada (1.3 significa 30% a mais, 1.5 significa 50% a mais). Deixar os multiplicadores em 1.0 mantém o mesmo preço o ano inteiro.",
        },
        {
          label: "Passo Integrações",
          domKey: "assistente.passo.integracoes",
          explanation:
            "Liga o Channel Manager e aceita uma chave de API, se você já tiver uma em mãos. Pode ser pulado sem prejuízo: dá para configurar depois com calma na tela Channel Manager.",
          visual: { icon: ArrowRight, text: "Próximo", tone: "primary" },
        },
        {
          label: "Passo Concluído",
          domKey: "assistente.passo.conclusao",
          explanation:
            "Confirmação de que a configuração mínima está feita, com o atalho para a visão geral. A partir daqui a pousada já opera — o que faltar (pagamentos, WhatsApp, site) fica em Configurações.",
        },
      ],
    },
  ],
  "/notificacoes": [
    {
      title: "O que esta tela faz",
      description:
        "É a sua caixa de avisos DO sistema: check-in feito, manutenção aberta, alerta operacional. Não confunda com a aba Notificações em Configurações, que decide o que é enviado AO HÓSPEDE — esta aqui é o que o sistema conta para você.",
      items: [
        {
          label: 'Botão "Marcar tudo como lido"',
          domKey: "historico-notificacoes.marcar_todas",
          visual: { icon: CheckCheck, text: "Marcar tudo como lido", tone: "outline" },
          explanation:
            "Só aparece quando existe pelo menos uma não lida na página. Zera o contador do sininho sem apagar nada — o histórico continua todo aqui.",
        },
        {
          label: "Marcar uma como lida",
          domKey: "historico-notificacoes.marcar_uma",
          visual: { icon: Check, text: "Marcar como lida", tone: "outline" },
          explanation:
            "Aparece ao passar o mouse sobre uma notificação ainda não lida (a que tem a barrinha colorida à esquerda e o pontinho ao lado do título). Útil para ir baixando a lista sem perder de vista o que ainda não foi tratado.",
        },
        {
          label: "Remover (ícone de lixeira)",
          domKey: "historico-notificacoes.remover",
          visual: { icon: Trash2, text: "Remover", tone: "danger" },
          explanation:
            "Apaga a notificação da lista em definitivo — é ação diferente de marcar como lida, e não tem desfazer. Se a intenção é só tirar do caminho, marque como lida.",
        },
        {
          label: "Paginação",
          domKey: "historico-notificacoes.paginacao",
          explanation:
            "A lista vem em blocos de 20. \"Marcar tudo como lido\" age sobre as suas notificações, mas o botão só aparece se houver alguma não lida na página que está aberta — em histórico longo, pode ser preciso avançar para encontrá-las.",
        },
      ],
    },
  ],
};
